/* eslint-disable import/no-unresolved */
import { Helmet } from 'react-helmet-async';

export default function UsedCarInspectionPage() {
  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": [
      {
        "@type": "Question",
        "name": "Why do I need an inspection for a used car in Kerala?",
        "acceptedAnswer": { "@type": "Answer", "text": "Used cars in Kerala often come with hidden issues — tampered odometers, undisclosed accident history, repainted panels hiding structural damage, and document fraud. An independent inspection protects you before you pay." }
      },
      {
        "@type": "Question",
        "name": "What does a used car inspection cover?",
        "acceptedAnswer": { "@type": "Answer", "text": "Our 130+ point inspection covers engine condition, transmission, brakes, suspension, electrical systems, body and paint for accident history, tyres, interior, and full documentation verification including service records and odometer consistency." }
      },
      {
        "@type": "Question",
        "name": "Can you detect if a used car has been in an accident?",
        "acceptedAnswer": { "@type": "Answer", "text": "Yes. Our inspectors check for panel gaps, repainted sections, structural damage, and chassis irregularities that indicate past accidents — even when the seller has not disclosed them." }
      },
      {
        "@type": "Question",
        "name": "How much does a used car inspection cost in Kerala?",
        "acceptedAnswer": { "@type": "Answer", "text": "Focus Motors offers two packages: Normal Inspection at Rs.4,999 and Premium Inspection at Rs.7,999. The right package depends on your car type — our team will guide you." }
      },
      {
        "@type": "Question",
        "name": "Do you inspect used cars across all of Kerala?",
        "acceptedAnswer": { "@type": "Answer", "text": "Yes, we offer used car inspection services across all of Kerala. Our team visits the car location — you do not need to bring it anywhere." }
      }
    ]
  };

  const serviceSchema = {
    "@context": "https://schema.org",
    "@type": "Service",
    "serviceType": "Used Car Inspection",
    "provider": { "@type": "Organization", "name": "Focus Motors", "url": "https://focusmotors.in" },
    "areaServed": { "@type": "State", "name": "Kerala" },
    "url": "https://focusmotors.in/used-car-inspection-kerala",
    "description": "Professional used car inspection across Kerala. 130+ point check covering engine, accident history, odometer, electricals, and documentation.",
    "offers": [
      { "@type": "Offer", "name": "Normal Inspection", "price": "4999", "priceCurrency": "INR", "url": "https://focusmotors.in/inspection-booking" },
      { "@type": "Offer", "name": "Premium Inspection", "price": "7999", "priceCurrency": "INR", "url": "https://focusmotors.in/inspection-booking" }
    ]
  };

  return (
    <>
      <Helmet>
        <title>Used Car Inspection in Kerala | Focus Motors</title>
        <meta
          name="description"
          content="Professional used car inspection across Kerala. 130+ point check for accident history, odometer tampering and engine condition. Normal Rs.4,999 | Premium Rs.7,999."
        />
        <link rel="canonical" href="https://focusmotors.in/used-car-inspection-kerala" />
        <meta property="og:title" content="Used Car Inspection in Kerala | Focus Motors" />
        <meta property="og:description" content="130+ point used car inspection across Kerala. Detect hidden accidents, odometer fraud and engine issues before you buy." />
        <meta property="og:url" content="https://focusmotors.in/used-car-inspection-kerala" />
        <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(serviceSchema)}</script>
      </Helmet>

      <div style={{ maxWidth: '900px', margin: '0 auto', padding: '40px 20px', fontFamily: 'inherit' }}>

        <h1>Used Car Inspection in Kerala</h1>
        <p>
          Buying a used car in Kerala? Do not rely on the seller alone.
          Focus Motors provides professional used car inspections across Kerala —
          a thorough 130+ point check that uncovers hidden problems before you pay.
        </p>

        <h2>Why Used Cars Need a Professional Inspection</h2>
        <ul>
          <li><strong>Odometer tampering</strong> — actual mileage hidden to inflate value</li>
          <li><strong>Undisclosed accident history</strong> — repainted panels hiding structural damage</li>
          <li><strong>Engine and transmission wear</strong> — issues masked temporarily before sale</li>
          <li><strong>Electrical faults</strong> — hidden wiring problems, sensor failures, battery issues</li>
          <li><strong>Document fraud</strong> — fake service records, RC irregularities</li>
        </ul>

        <h2>What Our 130+ Point Inspection Covers</h2>
        <ul>
          <li>Engine and Transmission — condition, leaks, noise, fluid levels</li>
          <li>Accident and Body Check — panel gaps, repainted sections, chassis alignment, rust</li>
          <li>Odometer Verification — consistency check against wear patterns and service records</li>
          <li>Electrical Systems — battery, wiring, lights, sensors, infotainment</li>
          <li>Brakes and Suspension — wear, response, alignment</li>
          <li>Tyres and Wheels — tread depth, uneven wear</li>
          <li>Interior and Safety — AC, airbags, seatbelts, locks, dashboard warnings</li>
          <li>Documentation — RC, insurance, service history, ownership records</li>
        </ul>

        <h2>Inspection Packages</h2>
        <p><strong>Normal Inspection — Rs.4,999</strong><br />
        Complete 130+ point inspection. Recommended for most used car purchases.</p>
        <p><strong>Premium Inspection — Rs.7,999</strong><br />
        Extended diagnostics. Recommended for premium or high-value used cars.</p>
        <a href="https://focusmotors.in/inspection-booking"
          style={{ display: 'inline-block', marginTop: '10px', padding: '12px 24px', background: '#000', color: '#fff', textDecoration: 'none', borderRadius: '6px' }}>
          Book Used Car Inspection
        </a>

        <h2>How It Works</h2>
        <ol>
          <li><strong>Book Online</strong> — share the car details and seller location</li>
          <li><strong>On-Site Inspection</strong> — our inspector visits the car location</li>
          <li><strong>Detailed Report</strong> — receive a 130+ point report with photos</li>
          <li><strong>Buy or Negotiate</strong> — use the findings to make an informed decision</li>
        </ol>

        <h2>Available Across All of Kerala</h2>
        <p>
          Our used car inspection service covers all districts of Kerala —
          Kochi, Thrissur, Kozhikode, Thiruvananthapuram, Kollam, Palakkad and more.
          Our inspector comes to the car location. No need to move the vehicle.
        </p>

        <h2>Frequently Asked Questions</h2>

        <h3>Why do I need an inspection for a used car in Kerala?</h3>
        <p>Used cars in Kerala often come with hidden issues — tampered odometers, undisclosed accident history, repainted panels hiding structural damage, and document fraud. An independent inspection protects you before you pay.</p>

        <h3>What does a used car inspection cover?</h3>
        <p>Our 130+ point inspection covers engine condition, transmission, brakes, suspension, electrical systems, body and paint for accident history, tyres, interior, and full documentation verification.</p>

        <h3>Can you detect if a used car has been in an accident?</h3>
        <p>Yes. Our inspectors check for panel gaps, repainted sections, structural damage, and chassis irregularities — even when the seller has not disclosed them.</p>

        <h3>How much does a used car inspection cost in Kerala?</h3>
        <p>Normal Inspection at Rs.4,999 and Premium Inspection at Rs.7,999. Our team will guide you to the right package based on your car type.</p>

        <h3>Do you inspect used cars across all of Kerala?</h3>
        <p>Yes, we cover all of Kerala. Our team visits the car location — you do not need to bring it anywhere.</p>

        <div style={{ marginTop: '40px', padding: '24px', background: '#f5f5f5', borderRadius: '8px' }}>
          <p style={{ margin: 0 }}><strong>Do not buy a used car on hope — buy it on facts.</strong></p>
          <a href="https://focusmotors.in/inspection-booking"
            style={{ display: 'inline-block', marginTop: '12px', padding: '12px 24px', background: '#000', color: '#fff', textDecoration: 'none', borderRadius: '6px' }}>
            Book Now
          </a>
        </div>

      </div>
    </>
  );
}